import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Genova AI Studio - AI Content Creation Platform | Generate Images, Voice & Scripts',
  description = 'Create stunning AI-generated images, realistic voiceovers, and engaging scripts with Genova AI Studio. The #1 AI content creation platform. Start free today!',
  keywords = 'AI content creation, AI image generator, AI voice generator, AI script writer, text to image, text to speech, AI art generator, content creation tool, AI creative studio',
  canonicalUrl,
  ogImage = 'https://storage.googleapis.com/gpt-engineer-file-uploads/gwD6XkgcCCcAWdnh4A0IrJQ5hxW2/uploads/1765115203704-1000014950.png',
  ogType = 'website',
  noindex = false,
}) => {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to set meta tags
    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    // Core meta
    setMeta('description', description);
    setMeta('keywords', keywords);
    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Open Graph
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', 'Genova AI Studio', true);
    setMeta('og:locale', 'en_US', true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
    }

    return () => {
      // Cleanup canonical on unmount
      if (canonical && canonicalUrl) {
        canonical.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noindex]);

  return null;
};

export default SEOHead;
